import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import { FiCheck, FiCopy, FiShoppingCart } from "react-icons/fi";
import "./ListaCompra.css";

/*
 * Lista de la compra: lo que hay que comprar para seguir el plan de comidas,
 * ordenado por el pasillo del súper.
 *
 * Lo tachado se guarda en el móvil (no en el servidor): es una ayuda mientras
 * se recorre el pasillo, no un dato que haga falta en ningún otro sitio.
 */

const PERIODOS = [
  [3, "3 días"],
  [7, "1 semana"],
  [14, "2 semanas"],
  [30, "1 mes"],
];

const ICONOS = {
  "fruta-verdura": "🥬",
  carniceria: "🥩",
  pescaderia: "🐟",
  "lacteos-huevos": "🥚",
  panaderia: "🍞",
  despensa: "🫙",
  congelados: "🧊",
  "especias-condimentos": "🧂",
  bebidas: "🥤",
};

const clave = (dias) => `titan-compra-${dias}`;

function leerTachados(dias) {
  try {
    return new Set(JSON.parse(localStorage.getItem(clave(dias)) || "[]"));
  } catch {
    return new Set();
  }
}

function ListaCompra() {
  const [dias, setDias] = useState(7);
  const [lista, setLista] = useState(null);
  const [tachados, setTachados] = useState(() => leerTachados(7));
  const [error, setError] = useState("");
  const [copiado, setCopiado] = useState(false);

  const cargar = useCallback(async () => {
    setLista(null);
    try {
      const { token } = await getSession();
      const res = await axios.get(`${API_URL}/compra`, {
        params: { dias },
        headers: { Authorization: `Bearer ${token}` },
      });
      setLista(res.data);
      setError("");
    } catch {
      setError("No se pudo preparar la lista.");
      setLista({ secciones: [], basicos: [], sinClasificar: [] });
    }
  }, [dias]);

  useEffect(() => {
    cargar();
    setTachados(leerTachados(dias));
  }, [cargar, dias]);

  const alternar = (nombre) => {
    setTachados((prev) => {
      const nuevo = new Set(prev);
      nuevo.has(nombre) ? nuevo.delete(nombre) : nuevo.add(nombre);
      try {
        localStorage.setItem(clave(dias), JSON.stringify([...nuevo]));
      } catch {
        // Navegador en privado o almacenamiento lleno: se pierde al salir,
        // pero la lista sigue funcionando
      }
      return nuevo;
    });
  };

  const copiar = async () => {
    try {
      const { token } = await getSession();
      const res = await axios.get(`${API_URL}/compra/texto`, {
        params: { dias },
        headers: { Authorization: `Bearer ${token}` },
        responseType: "text",
      });
      await navigator.clipboard.writeText(res.data);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      setError("No se pudo copiar. Prueba a seleccionar la lista a mano.");
    }
  };

  if (lista === null) return <div className="app-estado">Preparando tu lista…</div>;

  const totalArticulos = (lista.secciones || []).reduce((t, s) => t + s.articulos.length, 0);
  const hechos = [...tachados].length;

  return (
    <div className="lc">
      {/* Periodo */}
      <div className="lc-periodos">
        {PERIODOS.map(([v, t]) => (
          <button
            key={v}
            type="button"
            className={dias === v ? "lc-periodo-on" : ""}
            onClick={() => setDias(v)}
          >
            {t}
          </button>
        ))}
      </div>

      {totalArticulos === 0 ? (
        <section className="lc-vacio">
          <FiShoppingCart />
          <h2>Sin lista todavía</h2>
          <p>{lista.aviso || "Necesitas un plan de comidas para generar la compra."}</p>
        </section>
      ) : (
        <>
          <header className="lc-cabecera">
            <div>
              <h2>
                {totalArticulos} {totalArticulos === 1 ? "producto" : "productos"}
              </h2>
              <p>
                Para {lista.dias} {lista.dias === 1 ? "día" : "días"} de plan
                {hechos > 0 && ` · ${hechos} en el carro`}
              </p>
            </div>
            <button type="button" className="lc-copiar" onClick={copiar}>
              {copiado ? <FiCheck /> : <FiCopy />}
              {copiado ? "Copiada" : "Copiar"}
            </button>
          </header>

          {lista.secciones.map((seccion) => (
            <section key={seccion.id} className="lc-seccion">
              <h3>
                <span aria-hidden="true">{ICONOS[seccion.id] || "🛒"}</span>
                {seccion.nombre}
              </h3>
              <ul>
                {seccion.articulos.map((a) => {
                  const marcado = tachados.has(a.nombre);
                  return (
                    <li key={a.nombre}>
                      <button
                        type="button"
                        className={marcado ? "lc-art lc-art-ok" : "lc-art"}
                        onClick={() => alternar(a.nombre)}
                        aria-pressed={marcado}
                      >
                        <span className="lc-caja" aria-hidden="true">
                          {marcado && <FiCheck />}
                        </span>
                        <span className="lc-nombre">{a.nombre}</span>
                        {a.cantidad != null && (
                          <span className="lc-cantidad">
                            {a.cantidad} {a.unidad}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          {lista.sinClasificar?.length > 0 && (
            <section className="lc-seccion">
              <h3>
                <span aria-hidden="true">🛒</span>Otros
              </h3>
              <ul>
                {lista.sinClasificar.map((a) => {
                  const marcado = tachados.has(a.nombre);
                  return (
                    <li key={a.nombre}>
                      <button
                        type="button"
                        className={marcado ? "lc-art lc-art-ok" : "lc-art"}
                        onClick={() => alternar(a.nombre)}
                        aria-pressed={marcado}
                      >
                        <span className="lc-caja" aria-hidden="true">
                          {marcado && <FiCheck />}
                        </span>
                        <span className="lc-nombre">{a.nombre}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {lista.basicos?.length > 0 && (
            <section className="lc-basicos">
              <h3>Comprueba que tienes en casa</h3>
              <p>{lista.basicos.map((b) => b.nombre).join(" · ")}</p>
            </section>
          )}

          <p className="lc-aviso">{lista.aviso}</p>
        </>
      )}

      {error && <p className="app-estado-error">{error}</p>}
    </div>
  );
}

export default ListaCompra;
